import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { User } from '../types';
import {
  createUserWithEmailAndPassword,
  deleteUser,
  EmailAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  User as FirebaseUser,
} from 'firebase/auth';
import { onSnapshot, getDoc, setDoc, deleteDoc, doc } from 'firebase/firestore';
import { auth, db } from '../firebase';

interface AuthContextType {
  user: FirebaseUser | null;
  userData: User | null;
  isAdmin: boolean;
  isChildcareAdmin: boolean;
  isPastor: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (data: Partial<User>) => Promise<void>;
  deleteAccount: (password: string) => Promise<void>;
  isKioskMode: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = userData?.roles?.includes('admin') ?? false;
  const isChildcareAdmin = userData?.roles?.includes('childcare') ?? false;
  const isPastor = userData?.roles?.includes('pastor') ?? false;
  const isKioskMode = userData?.email === 'guestmode@nmzlakeland.org';

  useEffect(() => {
    let unsubUserDoc: (() => void) | null = null;

    const unsubAuth = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);

      // Clean up previous user doc listener
      if (unsubUserDoc) {
        unsubUserDoc();
        unsubUserDoc = null;
      }

      if (firebaseUser) {
        // Realtime listener for user document
        unsubUserDoc = onSnapshot(
          doc(db, 'users', firebaseUser.uid),
          (docSnap) => {
            if (docSnap.exists()) {
              const data = docSnap.data();
              if (data.banned) {
                signOut(auth);
                setUser(null);
                setUserData(null);
                setLoading(false);
                return;
              }
              if (data.dateOfBirth)
                data.dateOfBirth = data.dateOfBirth.toDate();
              if (data.createdOn) data.createdOn = data.createdOn.toDate();
              setUserData(data as User);
            } else {
              setUserData(null);
            }
            setLoading(false);
          }
        );
      } else {
        setUserData(null);
        setLoading(false);
      }
    });

    return () => {
      unsubAuth();
      if (unsubUserDoc) unsubUserDoc();
    };
  }, []);

  const login = async (email: string, password: string) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const userDoc = await getDoc(doc(db, 'users', cred.user.uid));
    if (userDoc.exists() && userDoc.data().banned) {
      await signOut(auth);
      throw new Error(
        'Your account has been banned. Please contact an administrator.'
      );
    }
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      const displayName = `${firstName} ${lastName}`;
      await updateProfile(userCredential.user, { displayName });

      const newUser = {
        userId: userCredential.user.uid,
        email: email,
        firstName,
        lastName,
        firstName_lc: firstName.toLowerCase(),
        lastName_lc: lastName.toLowerCase(),
        createdOn: new Date(),
        //roles: ["member"],
        familyId: crypto.randomUUID(),
        linkCode: generateCode(6).toLowerCase(),
        receiveCommunication: true,
      };

      await setDoc(doc(db, 'users', userCredential.user.uid), newUser);
    } catch (e) {
      console.log(e);
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setUserData(null);
  };

  function generateCode(length = 6) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';

    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return result;
  }
  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const deleteAccount = async (password: string) => {
    if (!user || !user.email) throw new Error('No user logged in');

    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);

    await deleteDoc(doc(db, 'users', user.uid));
    await deleteUser(user);

    setUser(null);
    setUserData(null);
  };

  const updateUserProfile = async (data: Partial<User>) => {
    if (!user) throw new Error('No user logged in');

    await setDoc(doc(db, 'users', user.uid), data, { merge: true });

    if (data.firstName || data.lastName) {
      const current = userData;
      const displayName = `${data.firstName ?? current?.firstName ?? ''} ${
        data.lastName ?? current?.lastName ?? ''
      }`.trim();
      await updateProfile(user, { displayName });
    }
  };

  const value: AuthContextType = {
    user,
    userData,
    isAdmin,
    isPastor,
    isChildcareAdmin,
    loading,
    login,
    register,
    logout,
    resetPassword,
    updateUserProfile,
    deleteAccount,
    isKioskMode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
